"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { Wrench } from "lucide-react";
import blogDataDe from "@data/pages/blog.json";
import blogDataEn from "@data/pages/blog.en.json";
import type { Locale } from "@utils/i18n/config";

const HeaderPublic = dynamic(() => import("@components/layout/HeaderPublic"), { ssr: true });
const FooterPublic = dynamic(() => import("@components/layout/FooterPublic"), { ssr: true });

interface BlogMaintenanceProps {
  locale: Locale;
}

/**
 * Shown in place of the blog listing while the WordPress backend is
 * unreachable. Rendered from the route's error.tsx, so the response carries a
 * 5xx status — see the SEO note there.
 *
 * It replaces an inline error state that sat inside the full listing page —
 * which rendered hero, trust section and CTAs around an empty grid and read
 * like the blog had no articles. A visitor who clicks "Blog" should be told
 * plainly that this is an outage on our side and that it is being worked on.
 *
 * Header and footer stay so the rest of the site is one click away.
 */
export default function BlogMaintenance({ locale }: BlogMaintenanceProps) {
  const { maintenance } = locale === "en" ? blogDataEn : blogDataDe;
  const blogHref = locale === "en" ? "/en/blog" : "/blog";

  return (
    <>
      <HeaderPublic />
      <main className="min-h-screen bg-white flex items-center" role="main">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <span
              className="inline-flex items-center gap-2 rounded-full bg-abis-accent/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-abis-accent"
              aria-hidden="true"
            >
              <Wrench className="w-3.5 h-3.5" />
              {maintenance.eyebrow}
            </span>

            <h1 className="mt-6 text-3xl md:text-4xl font-bold text-abis-primary leading-tight">
              {maintenance.title}
            </h1>

            <p className="mt-4 text-lg text-gray-600">{maintenance.description}</p>

            <div
              className="mt-8 flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-5 text-left"
              role="status"
            >
              <span className="relative mt-1.5 flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-abis-accent opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-abis-accent" />
              </span>
              <p className="text-sm text-gray-700">{maintenance.statusNote}</p>
            </div>

            {/* A plain anchor, not next/link: this must re-hit the server rather
                than restore a client-cached render of the same route. */}
            <a href={blogHref} className="btn btn-primary mt-8 inline-flex px-8 py-3 rounded-xl font-semibold">
              {maintenance.retryLabel}
            </a>

            <p className="mt-10 text-sm text-gray-500">{maintenance.linksIntro}</p>
            <ul className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2 list-none p-0">
              {maintenance.links.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-abis-accent font-semibold hover:underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>
      <FooterPublic locale={locale} />
    </>
  );
}
